import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import { closeModal } from "../../store/modal/modalReducer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

export default function ModalInvalidUnitTypes({ property_id, year_id, property_name }) {
    const dispatch = useDispatch();
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchInvalidUnitTypes();
    }, []);

    const fetchInvalidUnitTypes = async () => {
        try {
            setLoading(true);
            const response = await axios.post(`${process.env.REACT_APP_API_URL}invalid-unit-types`, {
                property_id,
                year_id,
            });
            setBookings(response.data.bookings);
        } catch (error) {
            console.error("Error fetching invalid unit types:", error);
        } finally {
            setLoading(false);
        }
    };

    const getIssueColor = (issueType) => {
        switch (issueType) {
            case "No unit type set":
                return "bg-yellow-100 text-yellow-800";
            case "Unit type deleted from system":
                return "bg-red-100 text-red-800";
            case "Unit type has no beds at this property":
                return "bg-orange-100 text-orange-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const openBooking = (bookingId) => {
        window.open(`/booking/${bookingId}`, "_blank");
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-[95vw] h-[95vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-orange-500 text-2xl" />
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Invalid Unit Types</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                {property_name} - Bookings with unit type issues
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => dispatch(closeModal())}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <FontAwesomeIcon icon={faTimes} className="text-2xl" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto p-6">
                    {loading ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-gray-500">Loading...</div>
                        </div>
                    ) : bookings.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-gray-300 text-6xl mb-4" />
                                <p className="text-gray-500 text-lg">No bookings with invalid unit types found</p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                <h3 className="font-semibold text-blue-900 mb-2">What are invalid unit types?</h3>
                                <ul className="text-sm text-blue-800 space-y-1">
                                    <li>• <strong>No unit type set:</strong> Booking has no unit type assigned (0 or NULL)</li>
                                    <li>• <strong>Unit type deleted:</strong> The unit type no longer exists in the system</li>
                                    <li>• <strong>No beds at property:</strong> Unit type exists but has no beds configured at this property</li>
                                </ul>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Booking
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Student
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Folder
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Unit Type
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Issue
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Properties with this Unit Type
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {bookings.map((booking) => (
                                            <tr
                                                key={booking.booking_id}
                                                className="hover:bg-gray-50 cursor-pointer"
                                                onClick={() => openBooking(booking.booking_id)}
                                            >
                                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                    <div className="text-blue-600 hover:text-blue-800 font-medium">
                                                        #{booking.booking_reference}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                    <div className="font-medium text-gray-900">
                                                        {booking.name} {booking.surname}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                    {booking.location}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                                    {booking.unit_type_name ? (
                                                        <div>
                                                            <div className="font-medium text-gray-900">
                                                                {booking.unit_type_name}
                                                            </div>
                                                            <div className="text-xs text-gray-500">ID: {booking.unit_type}</div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400 italic">
                                                            {booking.unit_type === 0 || booking.unit_type === null
                                                                ? "Not set"
                                                                : `ID: ${booking.unit_type} (deleted)`}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span
                                                        className={`px-2 py-1 text-xs font-medium rounded-full ${getIssueColor(
                                                            booking.issue_type
                                                        )}`}
                                                    >
                                                        {booking.issue_type}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-500">
                                                    {booking.properties_with_this_unit_type ? (
                                                        <div className="max-w-xs truncate" title={booking.properties_with_this_unit_type}>
                                                            {booking.properties_with_this_unit_type}
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400 italic">None</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                                <p className="text-sm text-gray-600">
                                    <strong>Total bookings with issues:</strong> {bookings.length}
                                </p>
                                <p className="text-sm text-gray-600 mt-2">
                                    Click on any row to open the booking in a new tab and fix the unit type.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
